import { useEffect, useState } from 'react';
import { api } from './api.js';
import { stamp } from './format.js';

/**
 * Objectives → strategies → tactics.
 *
 * The screen exists to answer one question at a glance: are we achieving what we said we
 * would? So every objective carries a rollup — strategies, tactics, done, late — and none
 * of those numbers is typed by anybody. They are counted from the action tracker through
 * the strategy each action is linked to, which means this screen cannot drift out of step
 * with the board. Closing an action here and closing it on the tracker are the same act.
 *
 * The tactic level deliberately has no "add" of its own: a tactic is an action, raised on
 * the tracker like every other action, and then linked. Offering a second way to create
 * one would produce two rows for one job within an hour of a real incident starting.
 */

const STATE_LABEL = { open: 'Open', done: 'Achieved', na: 'Withdrawn' };

/** The rollup, as a sentence rather than four unlabelled numbers. */
function Rollup({ o }) {
  if (!o.strategies && !o.tactics) {
    return <span className="rollup none">No strategies yet</span>;
  }
  return (
    <span className="rollup">
      {o.strategies} {o.strategies === 1 ? 'strategy' : 'strategies'}
      {o.tactics > 0 && <>, {o.tactics} {o.tactics === 1 ? 'tactic' : 'tactics'} — {o.tactics_done} done</>}
      {/* Only ever drawn when something really is past its target time. A counter that is
          permanently on screen showing zero stops being read within a day. */}
      {o.tactics_late > 0 && <span className="late"> · {o.tactics_late} LATE</span>}
    </span>
  );
}

export function Objectives({ incidentId, session, positions, onChanged }) {
  const [data, setData] = useState(null);
  const [error, setError] = useState(null);
  // Three states, and the distinction matters: undefined is "the form is closed", null is
  // "open, for the whole incident", a uuid is "open, for that function". Starting at null
  // rather than undefined is what put an open form on the screen before anybody asked.
  const [adding, setAdding] = useState(undefined);
  const [addingStrategy, setAddingStrategy] = useState(null); // objective id
  const [title, setTitle] = useState('');
  const [busy, setBusy] = useState(false);

  const mayIncident = session.permissions.includes('objective.write');
  const mayOwn = session.permissions.includes('objective.write.own');

  async function load() {
    try { setData(await api.objectives(incidentId)); }
    catch { setError('Could not load the objectives.'); }
  }
  useEffect(() => { load(); }, [incidentId]);

  if (error && !data) return <div className="card"><div className="empty">{error}</div></div>;
  if (!data) return <div className="card"><div className="empty">Loading…</div></div>;

  const held = data.held ?? [];
  const mayWriteFor = (positionId) =>
    mayIncident || (mayOwn && positionId != null && held.includes(positionId));

  const strategiesFor = (objectiveId) =>
    data.strategies.filter((s) => s.objective_id === objectiveId && s.state !== 'na');

  const incidentObjectives = data.objectives.filter((o) => o.position_id === null);
  const functionObjectives = data.objectives.filter((o) => o.position_id !== null);

  async function submitObjective(e) {
    e.preventDefault();
    if (!title.trim() || busy) return;
    setBusy(true); setError(null);
    try {
      await api.createObjective(incidentId, {
        title: title.trim(),
        positionId: adding ?? null,
      });
      setTitle(''); setAdding(undefined);
      await load(); onChanged?.();
    } catch (err) {
      setError(err.code === 'not_your_function'
        ? 'You can only set objectives for a function you are holding.'
        : 'That objective did not save.');
    } finally { setBusy(false); }
  }

  async function submitStrategy(e, objectiveId) {
    e.preventDefault();
    if (!title.trim() || busy) return;
    setBusy(true); setError(null);
    try {
      await api.createStrategy(incidentId, objectiveId, { title: title.trim() });
      setTitle(''); setAddingStrategy(null);
      await load(); onChanged?.();
    } catch {
      setError('That strategy did not save.');
    } finally { setBusy(false); }
  }

  async function setObjectiveState(o, state) {
    setError(null);
    try { await api.updateObjective(incidentId, o.id, { state }); await load(); onChanged?.(); }
    catch { setError('That change did not save.'); }
  }

  function ObjectiveCard({ o }) {
    const strategies = strategiesFor(o.id);
    const children = functionObjectives.filter((c) => c.parent_id === o.id);
    const mayWrite = mayWriteFor(o.position_id);

    return (
      <div className={`objective ${o.state}`}>
        <div className="oHead">
          <span className="oSeq">{o.position_code ? o.position_code.toUpperCase() : `O${o.seq}`}</span>
          <span className="oTitle">{o.title}</span>
          {o.state !== 'open' && <span className={`pill ${o.state}`}>{STATE_LABEL[o.state]}</span>}
          <div className="spacer" />
          <Rollup o={o} />
          {mayWrite && o.state === 'open' && (
            <>
              <button className="hoBtn" onClick={() => { setAddingStrategy(o.id); setTitle(''); }}>
                Add strategy
              </button>
              <button className="hoBtn" onClick={() => setObjectiveState(o, 'done')}>Achieved</button>
            </>
          )}
          {mayWrite && o.state !== 'open' && (
            <button className="hoBtn" onClick={() => setObjectiveState(o, 'open')}>Reopen</button>
          )}
        </div>

        {addingStrategy === o.id && (
          <form className="inlineAdd" onSubmit={(e) => submitStrategy(e, o.id)}>
            <input autoFocus value={title} onChange={(e) => setTitle(e.target.value)}
                   placeholder="The approach — how this objective will be achieved" />
            <button type="submit" disabled={busy || !title.trim()}>Add</button>
            <button type="button" className="ghost" onClick={() => setAddingStrategy(null)}>Cancel</button>
          </form>
        )}

        {strategies.length > 0 && (
          <ul className="strategies">
            {strategies.map((s) => (
              <li key={s.id}>
                <div className="sHead">
                  <span className="sSeq">{o.seq}.{s.seq}</span>
                  <span>{s.title}</span>
                  {s.tactics.length === 0 && (
                    <span className="hint">
                      {/* Says where tactics come from, because there is no button here on
                          purpose and an empty list with no explanation reads as broken. */}
                      no tactics yet — raise an action on the tracker and link it here
                    </span>
                  )}
                </div>
                {s.tactics.length > 0 && (
                  <ul className="tactics">
                    {s.tactics.map((t) => (
                      <li key={t.id} className={t.state}>
                        <span className="num">#{t.seq}</span>
                        <span className={`tick ${t.state}`}>{t.state === 'done' ? '✓' : t.state === 'na' ? '–' : '○'}</span>
                        <span className="tTitle">{t.title}</span>
                        {t.owner_name && <span className="owner">{t.owner_name}</span>}
                        {t.due_at && (
                          <span className={`due ${t.late ? 'late' : ''}`}>
                            {t.late ? 'LATE ' : ''}{stamp(t.due_at)}
                          </span>
                        )}
                      </li>
                    ))}
                  </ul>
                )}
              </li>
            ))}
          </ul>
        )}

        {children.length > 0 && (
          <div className="children">
            {children.map((c) => <ObjectiveCard key={c.id} o={c} />)}
          </div>
        )}
      </div>
    );
  }

  // Function objectives that stand alone — the client's explicit decision that a section
  // may write one without waiting for the IC to create something to hang it under.
  const standalone = functionObjectives.filter((o) => o.parent_id === null);

  return (
    <div className="card objectives">
      <h2>
        Objectives
        {mayIncident && (
          <button className="hoBtn head" onClick={() => { setAdding(null); setTitle(''); }}>
            Set incident objective
          </button>
        )}
      </h2>

      {error && <p className="err inline">{error}</p>}

      <div className="body">
        {adding !== undefined && (
          <form className="inlineAdd top" onSubmit={submitObjective}>
            <select value={adding ?? ''} onChange={(e) => setAdding(e.target.value || null)}>
              {mayIncident && <option value="">For the whole incident</option>}
              {positions
                .filter((p) => mayIncident || held.includes(p.id))
                .map((p) => <option key={p.id} value={p.id}>{p.name}</option>)}
            </select>
            <input autoFocus value={title} onChange={(e) => setTitle(e.target.value)}
                   placeholder="What we are trying to achieve" />
            <button type="submit" disabled={busy || !title.trim()}>Set</button>
            <button type="button" className="ghost" onClick={() => setAdding(undefined)}>Cancel</button>
          </form>
        )}

        {data.objectives.length === 0 && (
          <div className="empty">
            No objectives set yet.
            {mayIncident
              ? ' The incident commander sets the overarching ones; each function can add its own.'
              : ' Your function can add its own once you are holding a seat.'}
          </div>
        )}

        {incidentObjectives.map((o) => <ObjectiveCard key={o.id} o={o} />)}

        {standalone.length > 0 && (
          <>
            <h3 className="sectionHead">Function objectives</h3>
            {standalone.map((o) => <ObjectiveCard key={o.id} o={o} />)}
          </>
        )}

        {/* Offered to a section lead who holds a seat, without needing the IC to have set
            anything first. */}
        {!mayIncident && mayOwn && held.length > 0 && adding === undefined && (
          <button className="hoBtn" onClick={() => { setAdding(held[0]); setTitle(''); }}>
            Add an objective for my function
          </button>
        )}
      </div>
    </div>
  );
}
